import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';

const TeacherAttendanceStatus = () => {
  const { currentUser, userProfile } = useRealtime();
  const [attendanceData, setAttendanceData] = useState({
    todayStatus: null,
    monthlyStats: {
      presentDays: 0,
      absentDays: 0,
      halfDays: 0,
      workingDays: 0,
      attendancePercentage: 0
    },
    recentAttendance: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser?.uid && userProfile?.role === 'teacher') {
      loadAttendanceData();
      setupRealtimeListener();
    }
  }, [currentUser, userProfile]);

  const setupRealtimeListener = () => {
    if (!currentUser?.uid) return;

    // Listen for real-time updates to teacher attendance
    const attendanceQuery = query(
      collection(db, 'teacherDailyAttendance'),
      where('teacherId', '==', currentUser.uid),
      orderBy('date', 'desc'),
      limit(30)
    );

    const unsubscribe = onSnapshot(attendanceQuery, (snapshot) => {
      const attendanceRecords = [];
      snapshot.forEach(doc => {
        attendanceRecords.push({ id: doc.id, ...doc.data() });
      });
      
      updateAttendanceStats(attendanceRecords);
    });

    return unsubscribe;
  };

  const loadAttendanceData = async () => {
    try {
      setLoading(true);
      
      // Get current month's attendance
      const currentDate = new Date();
      const currentMonth = currentDate.toISOString().slice(0, 7); // YYYY-MM format
      
      const attendanceQuery = query(
        collection(db, 'teacherDailyAttendance'),
        where('teacherId', '==', currentUser.uid)
      );
      
      const snapshot = await getDocs(attendanceQuery);
      const attendanceRecords = [];
      
      snapshot.forEach(doc => {
        attendanceRecords.push({ id: doc.id, ...doc.data() });
      });
      
      updateAttendanceStats(attendanceRecords);
      
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAttendanceStats = (attendanceRecords) => {
    const today = new Date().toISOString().split('T')[0];
    const currentMonth = new Date().toISOString().slice(0, 7);
    
    // Find today's attendance
    const todayAttendance = attendanceRecords.find(record => record.date === today);
    
    // Calculate monthly stats
    const monthlyRecords = attendanceRecords.filter(record => 
      record.date && record.date.startsWith(currentMonth)
    );
    
    let presentDays = 0;
    let absentDays = 0;
    let halfDays = 0;
    
    monthlyRecords.forEach(record => {
      switch (record.status) {
        case 'present':
          presentDays++;
          break;
        case 'absent':
          absentDays++;
          break;
        case 'half_day':
          halfDays++;
          break;
      }
    });
    
    // Calculate working days for current month (excluding Sundays)
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    let workingDays = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      if (date.getDay() !== 0) { // Exclude Sundays
        workingDays++;
      }
    }
    
    const totalPresentDays = presentDays + (halfDays * 0.5);
    const attendancePercentage = workingDays > 0 ? Math.round((totalPresentDays / workingDays) * 100) : 0;
    
    // Get recent attendance (last 7 days)
    const recentAttendance = attendanceRecords
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 7);
    
    setAttendanceData({
      todayStatus: todayAttendance?.status || null,
      monthlyStats: {
        presentDays,
        absentDays,
        halfDays,
        workingDays,
        attendancePercentage
      },
      recentAttendance
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'absent':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'half_day':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'absent':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'half_day':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          My Attendance
        </h3>
        <div className="flex items-center space-x-2">
          <TrendingUp className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {attendanceData.monthlyStats.attendancePercentage}% this month
          </span>
        </div>
      </div>

      {/* Today's Status */}
      <div className="mb-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              {getStatusIcon(attendanceData.todayStatus)}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Today's Status</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <div>
            {attendanceData.todayStatus ? (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(attendanceData.todayStatus)}`}>
                {attendanceData.todayStatus === 'half_day' ? 'Half Day' : 
                 attendanceData.todayStatus.charAt(0).toUpperCase() + attendanceData.todayStatus.slice(1)}
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                Not Marked
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">This Month</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {attendanceData.monthlyStats.presentDays}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Present</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {attendanceData.monthlyStats.halfDays}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Half Day</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {attendanceData.monthlyStats.absentDays}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Absent</p>
          </div>
        </div>
      </div>

      {/* Recent Attendance */}
      {attendanceData.recentAttendance.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Recent Days</h4>
          <div className="space-y-2">
            {attendanceData.recentAttendance.slice(0, 5).map((record) => (
              <div key={record.id} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(record.status)}
                  <span className="text-sm text-gray-900 dark:text-white">
                    {formatDate(record.date)}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                  {record.status === 'half_day' ? 'Half Day' : 
                   record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAttendanceStatus;